import { map } from 'lodash-es';
import { CrossReferencesOpinionOption } from '../../../../../../../../frontend/libs/shared/src/lib/types/product/crossess-refernces';

export interface CrossReference {
  product: {
    id: number;
    onCa: boolean;
    spendBracket: number;
    description: string;
    buyerProduct: boolean;
    number: string;
    seller: {
      id: number;
      name: string;
    };
    unitVolume: number;
    supplier: {
      id: number;
      name: string;
    };
    spend: number;
  };
  noMatch: boolean;
  crossedProducts: CrossedProduct[];
}

export interface CrossedProduct {
  id: number;
  packageName: string;
  priceIndicator: string;
  description: string;
  unitsInPackage: number;
  number: string;
  maxPrice: number;
  crossType: string;
  minPrice: number;
}

export interface GetCrossReferencesByUnspscResponse {
  crosses: CrossReference[];
}

export interface CrossReferencesCoverage {
  lstSellerXrefDataRow: CrossReferencesCoverageDataRow[];
}

export interface CrossReferencesCoverage_Mod {
  sellerId: number;
  sellerName: string;
  productsOffered: number;
  productsPurchase: number;
  productsCrossReferenced: number;
  productsCantCrossReference: number;
}

export interface CrossReferencesCoverageDataRow {
  productsWithNoMatch: number;
  productsPurchasedByBuyer: number;
  isRequestingSeller: boolean;
  productsOfferedInMarket: number;
  productsCrossedByYou: number;
  seller: {
    id: number;
    name: string;
  };
}

export default class CrossReferencesService {
  static $inject = ['$http'];

  baseUrl = '/aptitude/api/crossreferences/';

  constructor(private $http) {
    'ngInject';
  }

  addAlternateCrossReference(
    productId: number,
    crossedProductId: number,
    rfpId: number,
    options?: any,
  ) {
    return this.$http.post(
      `${this.baseUrl}product/${productId}/alternate/${crossedProductId}`,
      null,
      {
        params: {
          rfp: rfpId,
        },
      },
    );
  }

  /**
   * TODO: request to have data remapped more intuitively
   * suggest coverages: [] or []
   */
  getCoverage(rpfId: number, options?: any) {
    return this.$http.get(`${this.baseUrl}coverage`, {
      params: {
        rfp: rpfId,
      },
      cache: true,
    });
  }

  getCrossReferencesByUnspsc(
    rfpId: number,
    unspscId: number,
    buyerProduct: boolean,
    options?: any,
  ): ng.IPromise<GetCrossReferencesByUnspscResponse> {
    return this.$http.get(this.baseUrl, {
      params: {
        rfp: rfpId,
        unspsc: unspscId,
        buyerProduct: buyerProduct || false,
      },
      cache: true,
    });
  }

  // TODO: Replace this with getCoverage below and move the markup to a filter
  getXrefs(requestId: number): ng.IPromise<CrossReferencesCoverage_Mod[]> {
    return this.$http
      .get(`${this.baseUrl}coverage?rfp=${requestId}`)
      .then(function (response: CrossReferencesCoverage) {
        return map(response.lstSellerXrefDataRow, function (item, key) {
          return {
            sellerId: item.seller.id,
            sellerName: item.seller.name,
            productsOffered: item.productsOfferedInMarket, // the total number of products offered in the market by all sellers
            productsPurchased: item.productsPurchasedByBuyer,
            productsCrossReferenced: item.productsCrossedByYou, // this can not be higher than productsOfferedInMarket
            productsCantCrossReference: item.productsWithNoMatch,
          };
        });
      });
  }

  removeAlternateCrossReference(
    productId: number,
    crossedProductId: number,
    rfpId: number,
    options?: any,
  ) {
    // use of reserved word "delete" in shortcut method not allowed in IE so using full config
    return this.$http.delete(
      `${this.baseUrl}product/${productId}/alternate/${crossedProductId}`,
      {
        params: {
          rfp: rfpId,
        },
      },
    );
  }

  setCrossReferenceToNoMatch(productId: number, rfpId: number, options?: any) {
    return this.$http.post(
      `${this.baseUrl}product/${productId}/nomatch`,
      null,
      {
        params: {
          rfp: rfpId,
        },
      },
    );
  }

  setPrimaryCrossReference(
    productId: number,
    crossedProductId: number,
    rfpId: number,
    options?: any,
  ) {
    return this.$http.post(
      `${this.baseUrl}product/${productId}/primary/${crossedProductId}`,
      null,
      {
        params: {
          rfp: rfpId,
        },
      },
    );
  }

  removePrimaryCrossReference(
    productId: number,
    crossedProductId: number,
    rfpId: number,
    options?: any,
  ) {
    // use of reserved word "delete" in shortcut method not allowed in IE so using full config
    return this.$http.delete(
      `${this.baseUrl}product/${productId}/primary/${crossedProductId}`,
      {
        params: {
          rfp: rfpId,
        },
      },
    );
  }

  /**
   * @deprecated Use updateOpinion with clinicalFeedback
   * @link updateOpinion
   */
  updateVote(crossReferenceId: number, opinionType: string, rfpId?: number) {
    return this.$http.put(`${this.baseUrl}${crossReferenceId}/opinion`, {
      opinionType: opinionType,
      rfpId: rfpId,
    });
  }

  /**
   * @description Update the vote for a crossReference.
   * @param opinionType deprecated - use clinicalFeedback, this parameter might get removed
   * @param clinicalFeedback takes in an enum key provided by getOpinionOptions
   * @link getOpinionOptions
   */
  updateOpinion(
    crossReferenceId: number,
    clinicalFeedback?: string,
    rfpId?: number,
  ) {
    return this.$http.put(`${this.baseUrl}${crossReferenceId}/opinion`, {
      rfpId,
      clinicalFeedback,
    });
  }

  addNote(crossReferenceId: number, note: string) {
    return this.$http.post(
      `${this.baseUrl}${crossReferenceId}/notes`,
      { note: note },
      { intercept: true },
    );
  }

  addCross(fromProductId: number, toProductId: number, rfpId?: number) {
    return this.$http.post(
      `${this.baseUrl}`,
      { fromProductId: fromProductId, toProductId: toProductId, rfpId: rfpId },
      { intercept: true },
    );
  }

  getOpinionOptions(): CrossReferencesOpinionOption[] {
    return this.$http.get(`${this.baseUrl}opinionoptions`, {
      cache: true,
    });
  }
}
